/*
 * Copyright (c) 2025 AirAsia Berhad (Malaysia). All rights reserved.
 *
 * This software is proprietary and confidential. Unauthorized copying of this file,
 * via any medium, is strictly prohibited. Do not distribute, copy, modify, or reuse
 * any part of this code without explicit written permission from AirAsia Move.
 *
 * For internal use only.
 */

import { useCallback } from 'react';
import { useAccessToken, useApiKey } from './account';

import { describeError } from '../utils/describeError';
export interface AnonymousSsoCredentials {
  apiKey: string;
  clientId: string;
}

export interface SsoCredentials extends AnonymousSsoCredentials {
  accessToken: string;
}

/** Either of the above, for endpoints that work logged in or logged out. */
export type SsoCredentialsWithOptionalUser = AnonymousSsoCredentials & {
  accessToken?: string;
};

/** Shared by both hooks below: fetch and validate the api key and client id. */
const useResolveAnonymousCredentials = (caller: string) => {
  const apikeyQuery = useApiKey();

  return useCallback(async (): Promise<AnonymousSsoCredentials> => {
    if (apikeyQuery.error) {
      console.error(
        'Error fetching API - ',
        caller,
        ':',
        describeError(apikeyQuery.error),
      );
    }

    const apiKey = await apikeyQuery.getToken();

    if (!apiKey?.apiKey) throw new Error('Missing API keys');
    if (!apiKey?.id) throw new Error('Missing Client ID');

    return { apiKey: apiKey.apiKey, clientId: apiKey.id };
  }, [apikeyQuery, caller]);
};

/**
 * Api key, client id and the logged-in user's access token. Use this for
 * anything a signed-in user does. Throws when there is no session.
 */
export function useSsoCredentials(caller: string): {
  resolve: () => Promise<SsoCredentials>;
} {
  const resolveAnonymous = useResolveAnonymousCredentials(caller);
  const accessTokenQuery = useAccessToken(caller);

  const resolve = useCallback(async (): Promise<SsoCredentials> => {
    if (accessTokenQuery.error) {
      console.error(
        'Error fetching access token - ',
        caller,
        ':',
        describeError(accessTokenQuery.error),
      );
    }

    const [anonymous, accessToken] = await Promise.all([
      resolveAnonymous(),
      accessTokenQuery.getToken(),
    ]);

    if (!accessToken) throw new Error('Missing access tokens');

    return { ...anonymous, accessToken };
  }, [accessTokenQuery, caller, resolveAnonymous]);

  return { resolve };
}

/**
 * Api key and client id only, for the few endpoints callable while logged out
 * — the forgot-password journey, where the user has no session yet. No access
 * token is fetched, so not having one is not an error.
 */
export function useAnonymousSsoCredentials(caller: string): {
  resolve: () => Promise<AnonymousSsoCredentials>;
} {
  return { resolve: useResolveAnonymousCredentials(caller) };
}
